import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Divider,
  Button,
  Grid,
  TextField,
  InputLabel,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import axios from "axios";
import { ReactComponent as FormIcon } from "../../../Icons/CommonFormIcon.svg";
import axiosInstance from "../../../api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { removeData } from "../../../Redux/Feature/AdminSlice";

export default function AnnouncementEdit() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the ID from the URL
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch()

  const allLanguages = useSelector((state) => state.language.languageList)

  const [activeLanguagesHeader, setActiveLanguagesHeader] = useState(allLanguages);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    mode: "onTouched",
  });

  const fetchAnnouncement = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}announcement/${id}`
      );
      const announcement = response.data.data;
      setValue("title", announcement.title);
      setValue("subtitle", announcement.subtitle);
      // Convert the date string to a Day.js object
      const date = dayjs(announcement.date, "DD-MM-YYYY,hh:mm A");
      setValue("dateTime", date);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if(error.response.status === 401 || error.response.status === 403){
        navigate('/Admin/login')
        dispatch(removeData())
    }
    }
  };

  useEffect(() => {
    fetchAnnouncement();
  }, [id]);

  const onSubmit = async (data) => {
    const updatedData = {
      title: data?.title,
      subtitle: data?.subtitle,
      date: dayjs(data?.dateTime).format("DD-MM-YYYY,hh:mm A"),
    };

    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_URL}announcement/${id}`,
        updatedData
      );
      console.log(response);
      navigate("/Admin");
    } catch (error) {
      console.log(error);
      if(error.response.status === 401 || error.response.status === 403){
        navigate('/Admin/login')
        dispatch(removeData())
    }
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Card
        sx={{ width: "80%", border: "1px solid #E5E7EB", borderRadius: "6px" }}
      >
        <CardContent style={{ padding: 0, margin: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: "#FCFCFD",
              padding: 2,
              border: "1px solid #E5E7EB",
              borderRadius: "6px 6px 0px 0px",
            }}
          >
            <FormIcon style={{ marginRight: "10px" }} />
            <Typography variant="h6_1">Announcement Details</Typography>
          </Box>

          {/* <Divider /> */}
          <form onSubmit={handleSubmit(onSubmit)}>
          {activeLanguagesHeader.map((lang) => (
            <Box key={lang.value}>
              <Typography sx={{fontWeight : 700, fontSize : '1.4rem', mt : 2, ml : 2}}>{lang.name}</Typography>
            <Box style={{ padding: "25px 17px 17px 17px" }}>
              <InputLabel
                sx={{
                  mb: 1,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {errors?.title?.[lang.value] ? "Title*" : "Title"}
              </InputLabel>
              <Controller
                name={`title.${lang.value}`}
                rules={{ required: true }}
                control={control}
                label
                render={({ field }) => (
                  <TextField
                    placeholder="Enter your title"
                    sx={{
                      mt: 0,
                      backgroundColor: "rgb(249, 250, 251)",
                      borderRadius: 0,
                      "& .MuiInputBase-input": {
                        color: errors?.title?.[lang.value] ? "red" : "black",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors?.title?.[lang.value]
                            ? "#ff5e74"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: errors?.title?.[lang.value]
                            ? "#ff5e74"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors?.title?.[lang.value]
                            ? "#ff5e74"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                      },
                    }}
                    error={Boolean(errors?.title?.[lang.value]) ? true : false}
                    {...field}
                  />
                )}
              />
              {errors?.title?.[lang.value] ? (
                <Typography style={{ color: "#ff5e74", margin: "0.3rem" }}>
                  Please enter title
                </Typography>
              ) : null}
            </Box>

            <Box style={{ padding: "0px 17px 17px" }}>
              <InputLabel
                sx={{
                  mb: 1,
                  mt: 3,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {errors?.subtitle?.[lang.value] ? "Subtitle*" : "Subtitle"}
              </InputLabel>
              <Controller
                name={`subtitle.${lang.value}`}
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <TextField
                    placeholder="Enter your subtitle"
                    sx={{
                      mt: 0,
                      backgroundColor: "rgb(249, 250, 251)",
                      borderRadius: 0,
                      "& .MuiInputBase-input": {
                        color: errors?.subtitle?.[lang.value] ? "red" : "black",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors?.subtitle?.[lang.value]
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: errors?.subtitle?.[lang.value]
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors?.subtitle?.[lang.value]
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                      },
                    }}
                    error={Boolean(errors?.subtitle?.[lang.value]) ? true : false}
                    {...field}
                  />
                )}
              />
              {errors?.subtitle?.[lang.value] ? (
                <Typography style={{ color: "#ff5e74", margin: "0.3rem" }}>
                  Please enter subtitle
                </Typography>
              ) : null}
            </Box>
            <Divider sx={{mt : 2, mb : 2}}/>
            </Box>
          ))}

            <Box style={{ padding: "0px 17px 17px" }}>
              <InputLabel
                sx={{
                  mb: 1,
                  mt: 3,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "black",
                }}
              >
                {errors?.dateTime ? "Date & Time*" : "Date & Time"}
              </InputLabel>
              <Controller
                name="dateTime"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    // label="Select Date and Time"

                    renderInput={(props) => (
                      <TextField
                        {...props}
                        error={Boolean(errors?.dateTime) ? true : false}
                        fullWidth
                        inputMode="none"
                      />
                    )}
                    sx={{
                      backgroundColor: "rgb(249, 250, 251)",
                      width: "100%",
                      ".MuiSvgIcon-root": { fill: "#1A2461" },
                      "& .MuiInputBase-input": {
                        color: errors?.dateTime ? "red" : "black",
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors?.dateTime
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover fieldset": {
                          borderColor: errors?.dateTime
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors?.dateTime
                            ? "red"
                            : "rgba(0, 0, 0, 0.23)",
                        },
                      },
                    }}
                    format="DD/MM/YYYY hh:mm A"
                  />
                )}
              />
              {errors?.dateTime ? (
                <Typography style={{ color: "#ff5e74", margin: "0.3rem" }}>
                  Please select Date and Time
                </Typography>
              ) : null}
            </Box>
            <Divider style={{ marginTop: "15px" }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "0px 17px 13px",
              }}
            >
              <Button
                variant="contained"
                type="submit"
                sx={{
                  padding: "10px 20px 10px 20px",
                  mt: 2,
                  width: "20%",
                  fontSize: 20,
                  backgroundColor: "rgb(14, 94, 132)",
                  ":hover": { bgcolor: "rgb(14, 94, 132)" },
                }}
              >
                Update
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
